import { AuthorityBadge } from'../components/AuthorityBadge';
import { Link } from'react-router-dom';
import { motion } from'framer-motion';
import { InteractiveInsightCard } from'../components/interactive/InteractiveInsightCard';

const fadeUp = {
 initial: { opacity: 0, y: 24 },
 animate: { opacity: 1, y: 0 },
};

// Interactive metric cards about Merkavah mysticism
const METRIC_CARDS = [
 {
 value:'7',
 label:'Heavenly Halls',
 sublabel:'Hekhalot',
 color:'violet',
 details: {
 explanation:'The seven heavens (Shamayim) are the celestial realms through which the mystic ascends to reach the divine throne. Each heaven - Vilon, Rakia, Shchakim, Zebul, Maon, Machon, and Aravot - contains distinct features, angelic beings, and spiritual realities.',
 example:'Aravot (the seventh heaven) is where the divine throne resides, along with the souls of the righteous',
 relatedConcepts: ['Heavenly Ascent','Angelic Hierarchy','Divine Throne'],
 references: [{ title:'Scholem - Major Trends in Jewish Mysticism' }],
 }
 },
 {
 value:'4',
 label:'Living Creatures',
 sublabel:'Chayot HaKodesh',
 color:'orange',
 details: {
 explanation:'The Chayot (Living Creatures) are the four angelic beings described in Ezekiel chapter 1. Each has four faces (human, lion, ox, eagle), four wings, and straight legs. They move as one, going wherever the spirit directs them, never turning as they move.',
 example:'The four faces represent different aspects: human (wisdom), lion (strength), ox (service), eagle (transcendence)',
 relatedConcepts: ['Ezekiel\'s Vision','Angelic Beings','Divine Chariot'],
 references: [{ title:'Ezekiel 1:4-14' }],
 }
 },
 {
 value:'∞',
 label:'Eyes on Wheels',
 sublabel:'Ophanim',
 color:'amber',
 details: {
 explanation:'The Ophanim (Wheels) are mysterious angelic beings described as wheel-within-wheel structures, their rims covered with countless eyes. They represent divine omniscience and move in harmony with the Chayot. The eyes symbolize God\'s all-seeing awareness.',
 example:'Wherever the spirit wished to go, the wheels would go, for the spirit of the living creatures was in the wheels',
 relatedConcepts: ['Divine Omniscience','Cosmic Motion','Angelic Forms'],
 references: [{ title:'Ezekiel 1:15-21' }],
 }
 },
 {
 value:'✨',
 label:'Divine Glory',
 sublabel:'Kavod',
 color:'cyan',
 details: {
 explanation:'The Kavod (Glory) represents the visible manifestation of God\'s presence. In Merkavah mysticism, it appears as brilliant light, fire, and radiance surrounding the divine throne. The mystic\'s ultimate goal is to behold this glory without being consumed.',
 example:'Like the appearance of a rainbow in the clouds on a rainy day, so was the radiance around him',
 relatedConcepts: ['Divine Presence','Hashmal','Throne of Glory'],
 references: [{ title:'Ezekiel 1:26-28' }],
 }
 }
];

// Topic cards about different aspects of Merkavah tradition
const TOPIC_CARDS = [
 {
 title:'The Divine Chariot',
 color:'orange',
 description:'Ezekiel\'s vision of the Merkavah - the celestial throne-chariot borne by angelic beings through the heavens, revealing the structure of divine reality.',
 details: {
 explanation:'The Merkavah (chariot) is not merely a vehicle but a dynamic system representing how the divine interacts with creation. The vision shows the Chayot (living creatures), Ophanim (wheels), the firmament above them, and the sapphire throne with a figure of fire and light upon it.',
 example:'The chariot imagery appears in Ezekiel 1, Ezekiel 10, Isaiah 6, and later in 1 Enoch and Hekhalot texts',
 relatedConcepts: ['Throne Mysticism','Angelic Hierarchy','Prophetic Vision'],
 references: [{ title:'Halperin - The Faces of the Chariot' }],
 }
 },
 {
 title:'Heavenly Ascent',
 color:'violet',
 description:'The mystical practice of"descending to the chariot" - journeying through celestial realms past angelic guardians to behold the divine throne.',
 details: {
 explanation:'Paradoxically called"descent" (yeridah), this ascent technique involved intense preparation, recitation of divine names and hymns, and passing through seven heavenly halls. Each hall has guardians who test the mystic\'s worthiness before allowing passage.',
 example:'Rabbi Akiva is said to have entered Paradise (the divine realm) and returned in peace, unlike his companions',
 relatedConcepts: ['Hekhalot Literature','Angel Passwords','Mystical Preparation'],
 references: [{ title:'Scholem - Jewish Gnosticism' }],
 }
 },
 {
 title:'The Seven Heavens',
 color:'amber',
 description:'The celestial architecture - seven distinct realms of increasing holiness, each with unique features, guardians, and spiritual realities.',
 details: {
 explanation:'Each heaven has a specific character: Vilon (curtain separating day and night), Rakia (where sun, moon, and stars are fixed), Shchakim (containing manna mills), Zebul (heavenly Jerusalem), Maon (angelic choirs), Machon (storehouses of elements), Aravot (containing the divine throne).',
 example:'The Talmud (Chagigah 12b) provides detailed descriptions of each heaven\'s contents and function',
 relatedConcepts: ['Celestial Geography','Cosmic Structure','Heavenly Halls'],
 references: [{ title:'Babylonian Talmud, Chagigah 12b' }],
 }
 },
 {
 title:'Sacred Hymns',
 color:'cyan',
 description:'The Kedushah and mystical songs - praise recited by angels and mystics, unlocking passage through heavenly gates.',
 details: {
 explanation:'The Kedushah ("Holy, Holy, Holy is the Lord of Hosts") is the central hymn of heavenly worship. The Hekhalot texts preserve elaborate hymns praising the divine, often using repetitive patterns and divine names. These were believed to have theurgic power.',
 example:'Kadosh Kadosh Kadosh Adonai Tzvaot - the Trisagion echoes through all seven heavens',
 relatedConcepts: ['Liturgical Mysticism','Divine Names','Angelic Worship'],
 references: [{ title:'Isaiah 6:3' }],
 }
 }
];

// Heritage cards about scholarly traditions
const HERITAGE_CARDS = [
 {
 title:'Hekhalot Literature',
 color:'amber',
 description:'Ancient Jewish mystical texts (3rd-7th century CE) describing heavenly halls, angelic beings, and techniques for mystical ascent.',
 details: {
 explanation:'The Hekhalot corpus includes major works like Hekhalot Rabbati (Greater Halls), Hekhalot Zutarti (Lesser Halls), Ma\'aseh Merkavah (Work of the Chariot), and Sefer Hekhalot (3 Enoch). These texts preserve esoteric traditions about heavenly geography and mystical practices.',
 example:'Hekhalot Rabbati describes Rabbi Ishmael\'s journey through the seven halls and his encounter with Metatron',
 relatedConcepts: ['Ancient Mysticism','Pseudepigrapha','Rabbinic Esotericism'],
 references: [{ title:'Peter Schafer - The Hidden and Manifest God' }],
 }
 },
 {
 title:'Academic Scholarship',
 color:'emerald',
 description:'Modern scholarly study of Jewish mysticism, from Gershom Scholem\'s foundational work to contemporary research.',
 details: {
 explanation:'Gershom Scholem (1897-1982) established Jewish mysticism as an academic field. His successors - Ithamar Gruenwald, David Halperin, Rachel Elior, Peter Schafer - have deepened understanding of Merkavah traditions through textual analysis and comparative study.',
 example:'Scholem identified Merkavah mysticism as the earliest stratum of Jewish mystical tradition, predating Kabbalah',
 relatedConcepts: ['Jewish Studies','Religious History','Text Criticism'],
 references: [{ title:'Scholem - Major Trends in Jewish Mysticism (1941)' }],
 }
 },
 {
 title:'Contemplative Tradition',
 color:'purple',
 description:'The living practice of visionary meditation - not merely historical study but engaged spiritual discipline.',
 details: {
 explanation:'Beyond academic study, some practitioners seek to revive Merkavah contemplative techniques. This involves careful study of texts, ethical preparation, and meditative practices focused on the chariot imagery. The goal is experiential understanding rather than mere intellectual knowledge.',
 example:'Traditional preparation includes fasting, ritual purity, and intensive study before attempting"descent to the chariot"',
 relatedConcepts: ['Meditation','Spiritual Practice','Living Tradition'],
 references: [{ title:'Elior - The Three Temples' }],
 }
 }
];

export function Home() {
 return (
 <div className="container-responsive py-12 sm:py-16 lg:py-20">
 <div className="relative overflow-hidden">
 <div className="absolute inset-0 -z-10">
 <div className="absolute top-[-20%] left-[20%] w-[500px] h-[500px] rounded-full bg-orange-500/5 blur-3xl" />
 <div className="absolute top-[10%] right-[10%] w-[400px] h-[400px] rounded-full bg-violet-500/5 blur-3xl" />
 <div className="absolute bottom-[-10%] left-[40%] w-[350px] h-[350px] rounded-full bg-[#3366cc]/5 blur-3xl" />
 </div>

 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ duration: 0.6 }}
 className="text-center max-w-4xl mx-auto"
 >
 <div className="inline-block mb-6 px-3 py-1 rounded-full border border-orange-400/30 bg-orange-500/5">
 <span className="text-xs font-medium text-orange-400">
 Visionary Traditions & Contemplative Studies
 </span>
 </div>

 <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6 leading-tight">
 <span className="text-[#202122] font-bold font-serif-flame">Mercava</span> Knowledge -
 <br className="hidden sm:block" /> Exploring Merkavah Mysticism
 </h1>

 <p className="text-base sm:text-lg text-slate-400 mb-4 max-w-2xl mx-auto leading-relaxed">
 Journey through Ezekiel's vision of the divine chariot, the seven heavenly halls,
 and ancient Jewish contemplative traditions. Explore the Chayot (living creatures),
 Ophanim (wheels), and the celestial architecture described in Hekhalot literature.
 </p>

 <p className="text-sm text-slate-500 mb-8 sm:mb-10">
 Built on scholarship from Gershom Scholem, David Halperin, Rachel Elior,
 and the rich textual tradition of Hekhalot and Merkavah writings.
 </p>

 <div className="flex flex-col sm:flex-row gap-4 justify-center">
 <Link
 to="/demo"
 className="btn-primary text-base sm:text-lg px-8 py-4 inline-block rounded-lg w-full sm:w-auto min-h-[44px]"
 >
 Begin the Journey
 </Link>
 </div>
 </motion.div>
 </div>

 <div className="w-24 h-px mx-auto mt-10 mb-2 bg-gradient-to-r from-transparent via-orange-400/40 to-transparent" />

 {/* E-E-A-T Authority Signals */}
 <AuthorityBadge
 lastUpdated="February 2026"
 researchInitiative="Mercava Knowledge Studies"
 sourceCount={10}
 domain="mercavaknowledge.com"
 />

 {/* Interactive Metric Cards */}
 <motion.div id="definition"
 variants={fadeUp}
 initial="initial"
 animate="animate"
 transition={{ duration: 0.6, delay: 0.2 }}
 className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-12 sm:mt-16 max-w-4xl mx-auto"
 >
 {METRIC_CARDS.map((item) => (
 <InteractiveInsightCard
 key={item.label}
 title={item.label}
 value={item.value}
 label={item.label}
 sublabel={item.sublabel}
 color={item.color}
 details={item.details}
 className="text-center"
 />
 ))}
 </motion.div>

 {/* The Vision Section */}
 <motion.section
 variants={fadeUp}
 initial="initial"
 animate="animate"
 transition={{ duration: 0.6, delay: 0.3 }}
 className="mt-16 sm:mt-20"
 >
 <div className="card border-orange-400/20 bg-slate-900/50 max-w-4xl mx-auto">
 <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
 <span className="text-3xl hebrew-text">{'מרכבה'}</span> The Divine Chariot Vision
 </h2>
 <p className="text-sm text-slate-400 mb-4">
 In the year 593 BCE, by the Chebar River in Babylon, the prophet Ezekiel beheld a vision
 that would become the foundation of Jewish mysticism. A stormy wind from the north,
 a great cloud with fire flashing, and within it - the appearance of four living creatures,
 wheels within wheels covered with eyes, and above them a throne of sapphire with a figure
 like fire upon it.
 </p>
 <p className="text-sm text-slate-400">
 This vision of the Merkavah (chariot) inspired centuries of mystical speculation and practice.
 The Hekhalot literature describes how later mystics sought to replicate Ezekiel's experience,
 developing techniques for ascending through the seven heavens to behold the divine throne.
 </p>
 </div>
 </motion.section>

 {/* Core Topics - Interactive */}
 <motion.section
 variants={fadeUp}
 initial="initial"
 animate="animate"
 transition={{ duration: 0.6, delay: 0.35 }}
 className="mt-16 sm:mt-20"
 >
 <div className="card border-violet-400/20 bg-slate-900/50 max-w-4xl mx-auto">
 <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
 Core Elements of Merkavah Mysticism
 </h2>
 <p className="text-sm text-slate-400 mb-6">
 Exploring the chariot vision, heavenly ascent, and contemplative tradition.
 <span className="text-orange-400 ml-1">Click any topic to learn more.</span>
 </p>
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
 {TOPIC_CARDS.map((topic) => (
 <InteractiveInsightCard
 key={topic.title}
 title={topic.title}
 description={topic.description}
 color={topic.color}
 details={topic.details}
 />
 ))}
 </div>
 </div>
 </motion.section>

 {/* Scholarly Heritage - Interactive */}
 <motion.section
 variants={fadeUp}
 initial="initial"
 animate="animate"
 transition={{ duration: 0.6, delay: 0.4 }}
 className="mt-16 sm:mt-20"
 >
 <div className="card border-amber-400/20 bg-slate-900/50 max-w-4xl mx-auto">
 <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
 Scholarly Traditions
 </h2>
 <p className="text-sm text-slate-400 mb-6">
 From ancient texts to modern academic study.
 <span className="text-[#3366cc] ml-1">Click to explore each tradition.</span>
 </p>
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
 {HERITAGE_CARDS.map((heritage) => (
 <InteractiveInsightCard
 key={heritage.title}
 title={heritage.title}
 description={heritage.description}
 color={heritage.color}
 details={heritage.details}
 />
 ))}
 </div>
 </div>
 </motion.section>

 {/* Educational Mission */}
 <motion.section
 variants={fadeUp}
 initial="initial"
 animate="animate"
 transition={{ duration: 0.6, delay: 0.45 }}
 className="mt-16 sm:mt-20"
 >
 <div className="card border-purple-400/20 bg-slate-900/50 max-w-4xl mx-auto">
 <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
 Our Educational Mission
 </h2>
 <div className="space-y-4 text-sm text-slate-400">
 <p>
 <strong className="text-orange-400">Accessibility:</strong> Merkavah mysticism
 has long been considered esoteric, reserved for those with advanced training.
 Our mission is to make these profound traditions accessible to all sincere seekers
 through interactive education and clear explanation.
 </p>
 <p>
 <strong className="text-[#3366cc]">Scholarship:</strong> We draw on the best
 academic scholarship - Scholem, Halperin, Elior, Schafer - while presenting
 material in an engaging, accessible format. Every claim is grounded in textual
 sources and scholarly consensus.
 </p>
 <p>
 <strong className="text-[#3366cc]">Respect:</strong> We approach this sacred tradition
 with the reverence it deserves. These are not merely historical curiosities but living
 teachings that continue to inspire spiritual seekers. We present them as wisdom worthy
 of serious engagement.
 </p>
 </div>
 </div>
 </motion.section>

 {/* Demo CTA */}
 <motion.div id="applications"
 variants={fadeUp}
 initial="initial"
 animate="animate"
 transition={{ duration: 0.6, delay: 0.5 }}
 className="mt-16 sm:mt-20 max-w-4xl mx-auto text-center"
 >
 <div className="card border-orange-400/30 bg-gradient-to-br from-orange-950/20 to-slate-900">
 <h3 className="text-xl font-bold text-orange-400 mb-3">Begin the Interactive Journey</h3>
 <p className="text-sm text-slate-300 mb-4">
 Walk through Ezekiel's vision step by step, explore the seven heavens,
 learn about the angelic beings, and discover the contemplative practices
 of the Merkavah mystics.
 </p>
 <Link
 to="/demo"
 className="btn-primary text-sm sm:text-base px-6 py-3 inline-block rounded-lg w-full sm:w-auto min-h-[44px]"
 >
 Start the Journey
 </Link>
 </div>
 </motion.div>

 {/* References Section */}
 <motion.section
 variants={fadeUp}
 initial="initial"
 animate="animate"
 transition={{ duration: 0.6, delay: 0.55 }}
 className="mt-16 sm:mt-20 max-w-4xl mx-auto"
 >
 <div className="card border-slate-600/20 bg-slate-900/30">
 <h3 className="text-lg font-bold text-slate-300 mb-4">Selected References</h3>
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 text-xs text-slate-500">
 <div>
 <p className="text-slate-400 font-medium">Primary Sources</p>
 <p>Ezekiel 1, 10 (Hebrew Bible)</p>
 <p>Hekhalot Rabbati, Hekhalot Zutarti</p>
 <p>Babylonian Talmud, Chagigah 11b-16a</p>
 </div>
 <div>
 <p className="text-slate-400 font-medium">Academic Studies</p>
 <p>Scholem. <em>Major Trends in Jewish Mysticism</em> (1941)</p>
 <p>Halperin. <em>The Faces of the Chariot</em> (1988)</p>
 </div>
 <div>
 <p className="text-slate-400 font-medium">Modern Scholarship</p>
 <p>Elior. <em>The Three Temples</em> (2004)</p>
 <p>Schafer. <em>The Hidden and Manifest God</em> (1992)</p>
 </div>
 <div>
 <p className="text-slate-400 font-medium">Contemplative Studies</p>
 <p>Gruenwald. <em>Apocalyptic and Merkavah Mysticism</em></p>
 <p>Wolfson. <em>Through a Speculum That Shines</em></p>
 </div>
 </div>
 </div>
 </motion.section>

 {/* Related Topics in the Knowledge Network */}
 <motion.section
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ duration: 0.6, delay: 0.6 }}
 className="mt-16 sm:mt-20 max-w-4xl mx-auto"
 >
 <div className="card border-slate-600/20 bg-slate-900/30 p-6 rounded-xl">
 <h3 className="text-lg font-bold text-slate-300 mb-2">Related Topics in the Knowledge Network</h3>
 <p className="text-sm text-slate-400 mb-4">Explore related subjects across the Global Knowledge Graph Network.</p>
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
 <a
 href="https://hashemlibrary.com"
 target="_blank"
 rel="noopener noreferrer"
 className="block p-4 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-blue-500/50 transition-all duration-300 hover:shadow-md"
 >
 <h4 className="font-semibold text-slate-900 dark:text-white mb-1">Hashem Library</h4>
 <p className="text-sm text-slate-500 dark:text-slate-400">Jewish learning and tradition</p>
 </a>
 <a
 href="https://godlibrary.com"
 target="_blank"
 rel="noopener noreferrer"
 className="block p-4 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-blue-500/50 transition-all duration-300 hover:shadow-md"
 >
 <h4 className="font-semibold text-slate-900 dark:text-white mb-1">God Library</h4>
 <p className="text-sm text-slate-500 dark:text-slate-400">Interfaith theology</p>
 </a>
 <a
 href="https://torahverse.com"
 target="_blank"
 rel="noopener noreferrer"
 className="block p-4 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-blue-500/50 transition-all duration-300 hover:shadow-md"
 >
 <h4 className="font-semibold text-slate-900 dark:text-white mb-1">Torah Verse</h4>
 <p className="text-sm text-slate-500 dark:text-slate-400">Torah textual analysis</p>
 </a>
 </div>
 <p className="text-xs text-slate-500 mt-4 text-center">
 <a href="https://globalknowledgegraph.com" className="text-blue-400 hover:underline transition-colors duration-300">
 Part of the Global Knowledge Graph Network
 </a>
 </p>
 </div>
 </motion.section>
 </div>
 );
}
